import React, { useState, useEffect } from 'react';
import { Container, Form, Row, Col, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '../../Components/MerchantButton';
import { compressAndUpload } from '../../Helpers/Utils/photoUpload';
import './UnifiedFormStyles.css';

const Merchandising = ({ navigation, primaryDetails, setPrimaryDetails }) => {
  // Initialize merchandising items array from primaryDetails or start empty
  const [merchandisingItems, setMerchandisingItems] = useState(() => {
    if (primaryDetails.merchandising_items && Array.isArray(primaryDetails.merchandising_items) && primaryDetails.merchandising_items.length > 0) {
      return primaryDetails.merchandising_items;
    }
    return [];
  });

   const [isEditing, setIsEditing] = useState(false);
  // Current merchandising item being edited (not yet added to table)
  const [currentItem, setCurrentItem] = useState({
    merchandising_type: '',
    merchandising_branding: '',
    merchandising_location: '',
    merchandising_photo_temp_path: ''
  });

  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      handleMerchandisingChange('merchandising_photo_temp_path', uploadResult.temp_path);
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  // Sync merchandisingItems to primaryDetails whenever merchandisingItems change
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
      merchandising_items: merchandisingItems,
    }));
  }, [merchandisingItems, setPrimaryDetails]);

  const merchandisingTypeOptions = [
    'P1 standee',
    'Apple Pay Standee',
    'P1 door hanger',
    'P1 door sticker',
    'QRPh door hanger',
    'Apple Pay door hanger',
    'QR door sticker',
    'Apple Pay door sticker',
    'DCC flyer',
    'Pay with Miles Flyer',
    'Maya Kita Club flyer',
    'P1 QR troubleshooting sticker',
    'QRPh QR troubleshooting sticker',
    'Clean Store Policy',
    'None'
  ];

  const brandingOptions = [
    '3.0',
    '2.0',
    '1.5',
    'Green Bird',
    'Customized'
  ];

  const locationOptions = [
    'glass door - eye level',
    'glass door - not eye level',
    'counter',
    'glass window',
    'others'
  ];

  // Handle current item field changes
  const handleMerchandisingChange = (field, value) => {
    setCurrentItem((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Add current item to table
  const handleAddItem = () => {
    const newErrors = {};

    if (!currentItem.merchandising_type) {
      newErrors.merchandising_type = "Merchandising Type is required";
    }

    // Only validate Branding if NOT a sticker, flyer, Clean Store Policy, or None
    const hideBranding = ['Apple Pay Standee', 'Apple Pay door hanger', 'Apple Pay door sticker','P1 door sticker', 'QRPh door sticker', 'P1 QR troubleshooting sticker', 'QRPh QR troubleshooting sticker', 'DCC flyer','Pay with Miles Flyer', 'Maya Kita Club flyer', 'Clean Store Policy', 'None'].includes(currentItem.merchandising_type);
    if (!hideBranding && !currentItem.merchandising_branding) {
      newErrors.merchandising_branding = "Branding is required";
    }

    // Only validate Location if NOT a flyer, Clean Store Policy, or None
    const hideLocation = ['DCC flyer', 'Pay with Miles Flyer', 'Maya Kita Club flyer', 'Clean Store Policy', 'None'].includes(currentItem.merchandising_type);
    if (!hideLocation && !currentItem.merchandising_location) {
      newErrors.merchandising_location = "Location is required";
    }

    // Only validate Photo Documentation if NOT Clean Store Policy or None
    const hidePhoto = ['Clean Store Policy', 'None'].includes(currentItem.merchandising_type);
    if (!hidePhoto && !currentItem.merchandising_photo_temp_path) {
      newErrors.merchandising_photo = "Photo Documentation is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return;
    }

    // Add to merchandisingItems array
    setMerchandisingItems([...merchandisingItems, { ...currentItem, id: Date.now() }]);

    // Reset current item form
    setCurrentItem({
      merchandising_type: '',
      merchandising_branding: '',
      merchandising_location: '',
      merchandising_photo_temp_path: ''
    });
    setErrors({});
    setIsEditing(false); 
    toast.success("Merchandising item added successfully!");
  };

  // Remove item from table
  const handleRemoveItem = (itemId) => {
    setMerchandisingItems(merchandisingItems.filter(item => item.id !== itemId));
  };

  const validate = () => {
    // Check if at least one item is added
    if (merchandisingItems.length === 0) {
      toast.error("Please add at least one merchandising item", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }

    return true;
  };

  const isFormValid = () => {
    return merchandisingItems.length > 0;
  };
  const handleBack = () => {
    if (navigation && navigation.previous) navigation.previous();
  };

  const handleNext = async () => {
    if (!validate()) return;
    
    // Check if any item is 'Clean Store Policy' and navigate to interested-maya-solution
    const hasCleanStorePolicy = merchandisingItems.some(item => item.merchandising_type === 'Clean Store Policy');
    if (hasCleanStorePolicy) {
      if (navigation && navigation.go) navigation.go('interested-maya-solution');
    } else {
      // if (navigation && navigation.go) navigation.go('qrph-knowledge');
      if (navigation && navigation.go) navigation.go('maya-black');
    }
  };


  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Merchandising</h4>
              <p className="form-desc">Please provide merchandising details</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>
                  {/* Form Section for Current Item */}
                  <div style={{ 
                    border: '2px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '20px', 
                    backgroundColor: '#f9f9f9',
                    marginBottom: '20px'
                  }}>
                    <h5 style={{ marginBottom: '20px', color: '#333' }}>
                      {merchandisingItems.length === 0 ? 'Item No. 1' : `Item No. ${merchandisingItems.length + 1}`}
                    </h5>

                    {/* Merchandising Type */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        Merchandising Type <span className="form-required">*</span>
                      </Form.Label>
                      <Form.Select
                        value={currentItem.merchandising_type || ''}
                        onChange={(e) => handleMerchandisingChange('merchandising_type', e.target.value)}
                        className={errors.merchandising_type ? 'form-select form-input-error' : 'form-select'}
                      >
                        <option value="">Select merchandising type</option>
                        {merchandisingTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.merchandising_type && (
                        <div className="form-error">{errors.merchandising_type}</div>
                      )}
                    </Form.Group>

                    {/* Branding - Hide for stickers, Clean Store Policy, and None */}
                    {!['Apple Pay Standee', 'Apple Pay door hanger', 'Apple Pay door sticker', 'P1 door sticker', 'QRPh door sticker', 'P1 QR troubleshooting sticker', 'QRPh QR troubleshooting sticker','Pay with Miles Flyer', 'DCC flyer', 'Maya Kita Club flyer', 'Clean Store Policy', 'None'].includes(currentItem.merchandising_type) && (
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Branding <span className="form-required">*</span>
                        </Form.Label>
                        <div>
                          {brandingOptions.map((branding) => (
                            <Form.Check
                              key={branding}
                              type="radio"
                              name="merchandising_branding"
                              label={branding}
                              value={branding}
                              checked={currentItem.merchandising_branding === branding}
                              onChange={(e) => handleMerchandisingChange('merchandising_branding', e.target.value)}
                              className="form-radio mb-2"
                            />
                          ))}
                        </div>
                        {errors.merchandising_branding && (
                          <div className="form-error">{errors.merchandising_branding}</div>
                        )}
                      </Form.Group>
                    )}

                    {/* Location - Hide for flyers, Clean Store Policy, and None */}
                    {!['DCC flyer', 'Maya Kita Club flyer', 'Clean Store Policy', 'Pay with Miles Flyer', 'None'].includes(currentItem.merchandising_type) && (
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Location <span className="form-required">*</span>
                        </Form.Label>
                        <div>
                          {locationOptions.map((location) => (
                            <Form.Check
                              key={location}
                              type="radio"
                              name="merchandising_location"
                              label={location}
                              value={location}
                              checked={currentItem.merchandising_location === location}
                              onChange={(e) => handleMerchandisingChange('merchandising_location', e.target.value)}
                              className="form-radio mb-2"
                            />
                          ))}
                        </div>
                        {errors.merchandising_location && (
                          <div className="form-error">{errors.merchandising_location}</div>
                        )}
                      </Form.Group>
                    )}

                    {/* Photo Documentation - Hide for Clean Store Policy and None */}
                    {!['Clean Store Policy', 'None'].includes(currentItem.merchandising_type) && (
                      <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        Photo Documentation <span className="form-required">*</span>
                        {currentItem.merchandising_photo_temp_path && !isUploading && (
                          <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                        )}
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className={errors.merchandising_photo ? 'form-input-error' : ''}
                        disabled={isUploading}
                      />
                      {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                      {errors.merchandising_photo && (
                        <div className="form-error">{errors.merchandising_photo}</div>
                      )}
                      </Form.Group>
                    )}

                    {/* Add Item Button */}
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Button 
                        variant="success" 
                        onClick={handleAddItem}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
                      >
                        <PlusOutlined /> {isEditing ? 'Save Item' : 'Add Item'}
                      </Button>
                    </div>
                  </div>

                  {/* Merchandising Items Table */}
                  {merchandisingItems.length > 0 && (
                    <div className="mb-4">
                      <h6 className="form-label" style={{ marginBottom: '15px' }}>Added Items</h6>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>No.</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Type</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Branding</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Location</th>
                              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {merchandisingItems.map((item, index) => (
                              <tr key={item.id} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{index + 1}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{item.merchandising_type || '-'}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{item.merchandising_branding || '-'}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{item.merchandising_location || '-'}</td>
                                <td style={{ padding: '12px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => {
                                      setIsEditing(true);
                                      setCurrentItem(item);
                                      handleRemoveItem(item.id);
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                  >
                                    Edit
                                  </Button>
                                  {merchandisingItems.length > 1 && (
                                    <Button 
                                      variant="danger" 
                                      size="sm"
                                      onClick={() => handleRemoveItem(item.id)}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                    >
                                      <DeleteOutlined /> Remove
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="form-nav-btns">
                    <AppButton onClick={handleBack} variant="back" className="form-btn variant-secondary">
                      ← Back
                    </AppButton> 
                    <AppButton onClick={handleNext} variant="next" className="form-btn variant-primary" disabled={!isFormValid()}>
                      Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Merchandising;
